import { lazy, Suspense, useCallback, useState } from 'react';
import { Outlet, Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from './SessionProvider';
import Login from './Login';
import UpdateNotice from './UpdateNotice';
import { WorkflowScope } from './WorkflowSafety';
import { AsyncButton, Icon } from '../components/ui';
import LiquidNavigation from '../components/LiquidNavigation';
import LaunchScreen from '../components/LaunchScreen';
import WorivaLogo from '../components/WorivaLogo';
const Dashboard = lazy(() => import('../features/dashboard/Dashboard'));
const Sets = lazy(() => import('../features/sets/Sets'));
const Add = lazy(() => import('../features/add/Add'));
const WordIndex = lazy(() => import('../features/index/WordIndex'));
const Study = lazy(() => import('../features/study/Study'));
const Practice = lazy(() => import('../features/study/Practice'));
const Quiz = lazy(() => import('../features/quiz/Quiz'));
const Multiplayer = lazy(() => import('../features/multiplayer/Multiplayer'));
const GuestJoin = lazy(() => import('../features/multiplayer/GuestJoin'));
const AccountPage = lazy(() => import('../features/account/AccountPage'));
const Dictionary = lazy(() => import('../features/dictionary/Dictionary'));

// 字典按鈕可切換：在字典頁再點一次即返回進入前的頁面
function DictionaryToggle() {
  const location = useLocation();
  const navigate = useNavigate();
  const open = location.pathname === '/dictionary';
  const cameFromApp = Boolean((location.state as { from?: string } | null)?.from);
  return (
    <Link
      to="/dictionary"
      state={{ from: `${location.pathname}${location.search}` }}
      className="icon-button"
      aria-label="字典"
      aria-current={open ? 'page' : undefined}
      onClick={(event) => {
        if (!open) return;
        event.preventDefault();
        if (cameFromApp) navigate(-1);
        else navigate('/', { replace: true });
      }}
    >
      <Icon name="book" />
    </Link>
  );
}

function Layout() {
  const { snapshot, online, notice } = useApp();
  const [introComplete, setIntroComplete] = useState(false);
  const finishIntro = useCallback(() => setIntroComplete(true), []);
  return (
    <div className="app">
      <header className="header">
        <Link to="/" className="brand" aria-label="Woriva，回首頁">
          <WorivaLogo />
        </Link>
        <div className="header-actions">
          <DictionaryToggle />
          <Link to="/account" className="avatar" aria-label="帳戶">
            {snapshot?.nickname.slice(0, 1) || <Icon name="user" />}
          </Link>
        </div>
      </header>
      <UpdateNotice />
      {!online ? <div className="sync-status">離線</div> : null}
      {notice ? (
        <p className="notice toast" role="status">
          {notice}
        </p>
      ) : null}
      <main className="content">
        <Suspense fallback={<p role="status">載入中…</p>}>
          <Outlet />
        </Suspense>
      </main>
      <LiquidNavigation />
      {!introComplete ? <LaunchScreen onComplete={finishIntro} /> : null}
    </div>
  );
}
export default function App() {
  const { session, snapshot, loading, error, refresh, logout } = useApp();
  const { pathname } = useLocation();
  if (pathname.startsWith('/join/')) return <Suspense fallback={<p role="status">載入中…</p>}><Routes><Route path="/join/:code" element={<GuestJoin />} /></Routes></Suspense>;
  if (!session) return <Login />;
  if (loading && !snapshot) return <LaunchScreen />;
  if (!snapshot)
    return (
      <main className="empty">
        <p role="alert">{error || '資料尚未載入'}</p>
        <AsyncButton action={refresh}>重試</AsyncButton>
        <AsyncButton action={logout}>返回登入</AsyncButton>
      </main>
    );
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="sets" element={<Sets />} />
        <Route path="sets/:setId" element={<Sets />} />
        <Route path="add" element={<WorkflowScope name="add"><Add /></WorkflowScope>} />
        <Route path="index" element={<WordIndex />} />
        <Route path="study/:setId" element={<WorkflowScope name="study"><Study /></WorkflowScope>} />
        <Route path="quiz" element={<WorkflowScope name="quiz"><Quiz /></WorkflowScope>} />
        <Route path="quiz/multiplayer" element={<WorkflowScope name="multiplayer"><Multiplayer /></WorkflowScope>} />
        <Route path="practice" element={<WorkflowScope name="practice"><Practice /></WorkflowScope>} />
        <Route path="account" element={<AccountPage />} />
        <Route path="dictionary" element={<WorkflowScope name="dictionary"><Dictionary /></WorkflowScope>} />
        <Route
          path="*"
          element={
            <p>
              找不到這個頁面。<Link to="/">回首頁</Link>
            </p>
          }
        />
      </Route>
    </Routes>
  );
}
